import { useState, useEffect, useCallback, useRef, FC } from 'react';
import { ToastProvider, useToast } from './components/ui/Toast';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppShell } from './components/layout/AppShell';
import { DashboardView } from './components/dashboard/DashboardView';
import { SettingsView } from './components/settings/SettingsView';
import { WhatsAppView } from './components/whatsapp/WhatsAppView';
import { GroupsView } from './components/groups/GroupsView';
import { OffersView } from './components/offers/OffersView';
import { QueueView } from './components/queue/QueueView';
import { BridgePagesView } from './components/bridge/BridgePagesView';
import { PublicBridgeView } from './components/bridge/PublicBridgeView';
import { PublicShowcaseView } from './components/showcase/PublicShowcaseView';
import { Tag } from 'lucide-react';
import {
  fetchWhatsAppStatus,
  requestWhatsAppDisconnect,
  WhatsAppStatus,
} from './api/whatsapp';
import { fetchGroups, WhatsAppGroup } from './api/groups';
import {
  fetchQueueItems,
  fetchCadenceTelemetry,
  pauseQueue,
  resumeQueue,
  QueueItem,
  QueueCadenceTelemetry,
} from './api/queue';
import {
  computeGlobalOperationalHealth,
  GroupItemLike,
  QueueItemLike,
} from './utils/operationalHealth';
import { getAffiliateSettings } from './utils/affiliateSettings';

const validTabs = [
  'dashboard',
  'whatsapp',
  'ofertas',
  'ofertas/cupons',
  'fila',
  'grupos',
  'grupos/monitor',
  'grupos/espelho',
  'groups',
  'bridge',
  'settings',
  'cupons',
];

const isNewOfferPath = (): boolean => {
  const p = window.location.pathname;
  return (
    p.includes('/offers/new') ||
    p.includes('/campaigns/new') ||
    p.includes('/ofertas/new')
  );
};

const isCouponsPath = (): boolean => {
  const p = window.location.pathname;
  return p.includes('/cupons');
};

const isMirrorPath = (): boolean => {
  const p = window.location.pathname;
  return (
    p.includes('/grupos/espelho') ||
    p.includes('/groups/mirror') ||
    p.includes('/espelho')
  );
};

const isConciliacaoPath = (): boolean => {
  const p = window.location.pathname;
  return (
    p.includes('/settings/conciliacao-csv') || p.includes('/conciliacao-csv')
  );
};

const getTabFromPath = (): string => {
  const full = window.location.pathname.replace(/^\/+/, '');
  if (
    full === 'grupos/espelho' ||
    full === 'groups/mirror' ||
    full === 'espelho'
  )
    return 'grupos/espelho';
  if (full === 'grupos/monitor' || full === 'groups/monitor')
    return 'grupos/monitor';
  if (full === 'ofertas/cupons' || full === 'cupons') return 'ofertas/cupons';
  if (full === 'settings/conciliacao-csv' || full === 'conciliacao-csv')
    return 'settings';
  const path = full.split('/')[0];
  if (path === 'groups') return 'grupos';
  if (path === 'campaigns' || path === 'fila') return 'fila';
  if (path === 'offers') return 'ofertas';
  if (path === 'cupons') return 'ofertas/cupons';
  if (path === 'espelho') return 'grupos/espelho';
  if (path === 'conciliacao-csv') return 'settings';
  if (validTabs.includes(full)) return full;
  if (validTabs.includes(path)) return path;
  return 'dashboard';
};

const placeholderConfig: Record<
  string,
  { icon: typeof Tag; title: string; description: string }
> = {};

const AuthenticatedApp: FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<string>(getTabFromPath);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(
    null
  );
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [cadenceTelemetry, setCadenceTelemetry] =
    useState<QueueCadenceTelemetry | null>(null);
  const [projectName, setProjectName] = useState<string>(
    () => getAffiliateSettings().projectName || 'Achadinhos Tech'
  );

  const lastHealthLoadTimeRef = useRef<number>(0);

  const loadOperationalHealthData = useCallback(
    async (force = false) => {
      if (!token) return;
      const now = Date.now();
      if (!force && now - lastHealthLoadTimeRef.current < 15000) {
        return;
      }
      lastHealthLoadTimeRef.current = now;

      try {
        const [waData, groupsData, queueData, cadenceData] = await Promise.all([
          fetchWhatsAppStatus(token).catch(() => null),
          fetchGroups(token).catch(() => null),
          fetchQueueItems(token).catch(() => null),
          fetchCadenceTelemetry(token).catch(() => null),
        ]);
        if (waData !== null) setWhatsappStatus(waData);
        if (groupsData !== null) setGroups(groupsData);
        if (queueData !== null) setQueueItems(queueData);
        if (cadenceData !== null) setCadenceTelemetry(cadenceData);
      } catch {
        // Silencioso
      }
    },
    [token]
  );

  const [offerAction, setOfferAction] = useState<'view' | 'edit'>('view');

  const handleTabChange = (
    newTab: string,
    offerId?: string | null,
    action: 'view' | 'edit' = 'view'
  ) => {
    setActiveTab(newTab);
    const mainTab = newTab.split('/')[0];
    if (mainTab === 'ofertas' && offerId) {
      setEditingOfferId(offerId);
      setOfferAction(action);
    } else if (mainTab !== 'ofertas') {
      setEditingOfferId(null);
      setOfferAction('view');
    }
    const targetPath = newTab === 'dashboard' ? '/' : `/${newTab}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setActiveTab(getTabFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Escuta alteração do nome do projeto/configurações
  useEffect(() => {
    const handleSettingsUpdated = () => {
      setProjectName(getAffiliateSettings().projectName || 'Achadinhos Tech');
    };
    window.addEventListener(
      'affiliate-settings-updated',
      handleSettingsUpdated
    );
    return () =>
      window.removeEventListener(
        'affiliate-settings-updated',
        handleSettingsUpdated
      );
  }, []);

  const refreshWhatsApp = async () => {
    if (!token) return;
    try {
      const data = await fetchWhatsAppStatus(token);
      setWhatsappStatus(data);
      await loadOperationalHealthData(true);
    } catch {
      // Ignora erro silencioso
    }
  };

  const disconnectWhatsApp = async () => {
    if (!token) return;
    try {
      await requestWhatsAppDisconnect(token);
      await refreshWhatsApp();
    } catch {
      // Trata erro
    }
  };

  // Loop de telemetria em tempo real para semáforos globais e de menus (a cada 30 segundos)
  useEffect(() => {
    loadOperationalHealthData(true);

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadOperationalHealthData(true);
      }
    }, 30000);

    const handleFocus = () => {
      loadOperationalHealthData(false);
    };

    const handleHealthRefresh = () => {
      loadOperationalHealthData(true);
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('operational-health-refresh', handleHealthRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(
        'operational-health-refresh',
        handleHealthRefresh
      );
    };
  }, [loadOperationalHealthData]);

  const { success, error: showError } = useToast();
  const [isTogglingQueue, setIsTogglingQueue] = useState(false);

  const handleToggleQueuePause = async () => {
    if (!token) return;
    setIsTogglingQueue(true);
    try {
      if (cadenceTelemetry?.is_paused) {
        await resumeQueue(token);
        success('Esteira de disparos retomada com sucesso.', 'Fila Ativa');
      } else {
        await pauseQueue(token);
        success('Esteira de disparos pausada.', 'Fila Pausada');
      }
      await loadOperationalHealthData();
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Falha ao alterar estado da esteira.';
      showError(message, 'Erro na Esteira');
    } finally {
      setIsTogglingQueue(false);
    }
  };

  const handleStatusChange = useCallback((newStatus: WhatsAppStatus) => {
    setWhatsappStatus(newStatus);
  }, []);

  const handleGroupsChange = useCallback((updatedGroups: WhatsAppGroup[]) => {
    setGroups(updatedGroups);
  }, []);

  const handleTelemetryChange = useCallback(
    (telemetry: QueueCadenceTelemetry) => {
      setCadenceTelemetry(telemetry);
    },
    []
  );

  const isConnected = whatsappStatus?.connected === true;

  // Calcula telemetria consolidada de saúde
  const operationalHealth = computeGlobalOperationalHealth({
    whatsappConnected: isConnected,
    groups: groups as GroupItemLike[],
    queueItems: queueItems as QueueItemLike[],
    isEnginePaused: cadenceTelemetry?.is_paused,
    outsideBusinessHours: cadenceTelemetry
      ? !cadenceTelemetry.business_hours_active
      : false,
  });

  const globalHealthTooltip = operationalHealth.globalMessage;

  const hasActiveGroups = groups.some((g) => g.status === 'active');
  const canToggleQueue = Boolean(
    token && cadenceTelemetry && isConnected && hasActiveGroups
  );
  const queueBlockedReason = !isConnected
    ? 'Conecte uma linha de WhatsApp para gerenciar a fila'
    : !hasActiveGroups
      ? 'Cadastre ou ative ao menos um grupo para operar a fila'
      : !cadenceTelemetry
        ? 'Aguardando telemetria da fila'
        : undefined;

  return (
    <AppShell
      activeTab={activeTab}
      onTabChange={handleTabChange}
      whatsappConnected={isConnected}
      whatsappPhone={whatsappStatus?.phone_number}
      activeProjectName={projectName}
      whatsappHealth={operationalHealth.whatsapp}
      groupsHealth={operationalHealth.groups}
      queueHealth={operationalHealth.queue}
      globalHealth={operationalHealth.global}
      globalTooltip={globalHealthTooltip}
      isQueuePaused={cadenceTelemetry?.is_paused}
      onToggleQueuePause={handleToggleQueuePause}
      isTogglingQueue={isTogglingQueue}
      canToggleQueue={canToggleQueue}
      queueBlockedReason={queueBlockedReason}
    >
      {activeTab === 'dashboard' && (
        <DashboardView
          whatsappConnected={isConnected}
          onNavigate={(tab, offerId) => handleTabChange(tab, offerId)}
          token={token || ''}
        />
      )}

      {activeTab === 'whatsapp' && (
        <WhatsAppView
          initialStatus={whatsappStatus}
          onStatusChange={handleStatusChange}
        />
      )}

      {(activeTab === 'grupos' ||
        activeTab.startsWith('grupos/') ||
        activeTab === 'groups' ||
        activeTab.startsWith('groups/')) && (
        <GroupsView
          initialGroups={groups}
          isWhatsAppConnected={isConnected}
          onGroupsChange={handleGroupsChange}
          initialSubTab={
            activeTab === 'grupos/espelho' || isMirrorPath()
              ? 'mirror'
              : activeTab === 'grupos/monitor' ||
                  (typeof window !== 'undefined' &&
                    (window.location.pathname.includes('/grupos/monitor') ||
                      window.location.pathname.includes('/groups/monitor')))
                ? 'monitor'
                : 'list'
          }
        />
      )}
      {(activeTab === 'ofertas' ||
        activeTab.startsWith('ofertas/') ||
        activeTab === 'offers' ||
        activeTab.startsWith('offers/')) && (
        <OffersView
          initialMode={
            editingOfferId && offerAction === 'edit'
              ? 'create'
              : isNewOfferPath()
                ? 'create'
                : 'list'
          }
          initialOfferId={editingOfferId}
          initialAction={offerAction}
          initialTab={
            activeTab === 'ofertas/cupons' || isCouponsPath()
              ? 'cupons'
              : 'offers'
          }
        />
      )}
      {(activeTab === 'fila' || activeTab === 'campaigns') && (
        <QueueView
          initialTelemetry={cadenceTelemetry}
          initialWhatsappStatus={whatsappStatus}
          onNavigate={(tab, offerId) => handleTabChange(tab, offerId)}
          onTelemetryChange={handleTelemetryChange}
        />
      )}

      {activeTab === 'bridge' && <BridgePagesView />}

      {placeholderConfig[activeTab] && (
        <div className="space-y-3 rounded-2xl border border-surface-border bg-surface-panel p-8 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-400">
            {(() => {
              const Icon = placeholderConfig[activeTab].icon;
              return <Icon className="h-5 w-5" />;
            })()}
          </div>
          <h3 className="text-base font-semibold text-content-main">
            {placeholderConfig[activeTab].title}
          </h3>
          <p className="mx-auto max-w-md text-xs leading-relaxed text-content-muted">
            {placeholderConfig[activeTab].description}
          </p>
        </div>
      )}

      {activeTab === 'settings' && (
        <SettingsView
          whatsappStatus={whatsappStatus}
          onRefreshWhatsApp={refreshWhatsApp}
          onDisconnectWhatsApp={disconnectWhatsApp}
          onNavigateToWhatsApp={() => handleTabChange('whatsapp')}
          initialTab={isConciliacaoPath() ? 'conciliacao-csv' : undefined}
        />
      )}
    </AppShell>
  );
};

const getPublicBridgeSlug = (): string | null => {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/p\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

const getPublicShowcaseSlug = (): string | null => {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;

  // Formato 1: /c/:tenant_slug ou /showcase/:tenant_slug
  const matchC = path.match(/^\/(?:c|showcase)\/([a-zA-Z0-9_-]+)/);
  if (matchC) return matchC[1];

  // Formato 2: /:tenant_slug/ofertas
  const matchOfertas = path.match(/^\/([a-zA-Z0-9_-]+)\/ofertas/);
  if (matchOfertas) return matchOfertas[1];

  return null;
};

export const App: FC = () => {
  const publicSlug = getPublicBridgeSlug();
  const showcaseSlug = getPublicShowcaseSlug();

  // Rota pública de vitrine (/c/:tenant_slug ou /:tenant_slug/ofertas) - Acessível sem login
  if (showcaseSlug) {
    return (
      <ErrorBoundary
        fallbackTitle="Erro na Vitrine"
        fallbackMessage="Não foi possível carregar a vitrine de ofertas solicitada."
      >
        <ThemeProvider>
          <PublicShowcaseView tenantSlug={showcaseSlug} />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Rota pública de alta conversão (/p/:slug) - Acessível sem login
  if (publicSlug) {
    return (
      <ErrorBoundary
        fallbackTitle="Erro na Página"
        fallbackMessage="Não foi possível carregar a página de destino solicitada."
      >
        <ThemeProvider>
          <PublicBridgeView slug={publicSlug} />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      fallbackTitle="Erro Crítico na Aplicação"
      fallbackMessage="Ocorreu um erro no nível raiz da interface do TrafficForge."
    >
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <ProtectedRoute>
              <AuthenticatedApp />
            </ProtectedRoute>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};
